import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { User, Shield, Lock, BarChart3, Crown, Save, Loader2, AlertTriangle, Trash2, MessageSquare, FlaskConical, Stethoscope, HelpCircle, Key, Cpu, X, Bell, Mail, Download, Smartphone, CreditCard, FileJson } from "lucide-react";
import { SECTORS, STRUCTURES, LLM_PROVIDERS } from "@/data/staticData";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TIER_QUOTAS = {
  free: { chat: 3, simulations: 1, diagnostics: 1, quiz: 1 },
  premium: { chat: 50, simulations: 20, diagnostics: 5, quiz: 999 },
  admin: { chat: 99999, simulations: 99999, diagnostics: 99999, quiz: 99999 },
};

const FEATURE_ICONS = { chat: MessageSquare, simulations: FlaskConical, diagnostics: Stethoscope, quiz: HelpCircle };
const FEATURE_LABELS = { chat: "Chat IA", simulations: "Simulations", diagnostics: "Diagnostics", quiz: "Quiz" };

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [structure, setStructure] = useState("");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [llmConfig, setLlmConfig] = useState(null);
  const [selectedLLM, setSelectedLLM] = useState("");
  const [llmApiKey, setLlmApiKey] = useState("");
  const [savingLLM, setSavingLLM] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [savingNotifs, setSavingNotifs] = useState(false);
  // 2FA / Subscription / RGPD state
  const [totpStatus, setTotpStatus] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null); // {secret, otpauth_uri}
  const [totpCode, setTotpCode] = useState("");
  const [totpLoading, setTotpLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { loadProfile(); loadTotpStatus(); loadSubscription(); }, []);

  const loadTotpStatus = async () => {
    try { const { data } = await API.get("/user/2fa/status"); setTotpStatus(data); } catch {}
  };

  const loadSubscription = async () => {
    try { const { data } = await API.get("/user/subscription"); setSubscription(data); } catch {}
  };

  const startTotpSetup = async () => {
    setTotpLoading(true);
    try {
      const { data } = await API.post("/user/2fa/setup");
      setTotpSetup(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setTotpLoading(false); }
  };

  const enableTotp = async () => {
    if (!totpCode || totpCode.length < 6) { toast.error("Entrez le code à 6 chiffres"); return; }
    setTotpLoading(true);
    try {
      await API.post("/user/2fa/enable", { secret: totpSetup.secret, code: totpCode });
      toast.success("2FA activée");
      setTotpSetup(null); setTotpCode("");
      loadTotpStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Code invalide"); }
    finally { setTotpLoading(false); }
  };

  const disableTotp = async () => {
    const code = window.prompt("Entrez votre code 2FA actuel pour désactiver");
    if (!code) return;
    try {
      await API.post("/user/2fa/disable", { code });
      toast.success("2FA désactivée");
      loadTotpStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const exportData = async () => {
    try {
      const resp = await API.get("/user/export-data", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ia-conforme-mes-donnees-${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Données exportées (RGPD Article 20)");
    } catch { toast.error("Erreur lors de l'export"); }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Confirmer l'annulation de votre abonnement Premium ?")) return;
    setCancelling(true);
    try {
      await API.post("/user/subscription/cancel");
      toast.success("Abonnement annulé");
      loadSubscription();
      window.location.reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setCancelling(false); }
  };

  const loadProfile = async () => {
    try {
      const { data } = await API.get("/user/profile");
      setProfile(data);
      setName(data.name || "");
      setSector(data.sector || "");
      setStructure(data.structure || "");
    } catch {}
    // Charger la config LLM perso si premium/admin
    try {
      const { data } = await API.get("/user/llm-config");
      setLlmConfig(data);
      if (data.configured) {
        setSelectedLLM(`${data.provider}:${data.model}`);
      }
    } catch {}
    // Charger les préférences de notification
    try {
      const { data } = await API.get("/user/notifications");
      setNotifPrefs(data);
    } catch {}
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await API.put("/user/profile", { name, sector, structure });
      setProfile(data);
      updateUser({ name, sector, structure });
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (newPwd !== confirmPwd) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (newPwd.length < 6) { toast.error("Minimum 6 caractères"); return; }
    setSavingPwd(true);
    try {
      await API.put("/user/password", { current_password: currentPwd, new_password: newPwd });
      toast.success("Mot de passe modifié");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingPwd(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await API.delete("/user/account");
      await logout();
      navigate("/login");
    } catch {
      toast.error("Erreur lors de la suppression");
      setDeleting(false);
    }
  };

  const saveLLMConfig = async () => {
    if (!selectedLLM || !llmApiKey.trim()) {
      toast.error("Sélectionnez un modèle et entrez votre clé API");
      return;
    }
    const [provider, model] = selectedLLM.split(":");
    setSavingLLM(true);
    try {
      await API.post("/user/llm-config", { provider, model, api_key: llmApiKey.trim() });
      toast.success("Configuration IA personnelle enregistrée");
      setLlmApiKey("");
      const { data } = await API.get("/user/llm-config");
      setLlmConfig(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingLLM(false); }
  };

  const deleteLLMConfig = async () => {
    try {
      await API.delete("/user/llm-config");
      setLlmConfig({ configured: false });
      setSelectedLLM("");
      setLlmApiKey("");
      toast.success("Configuration personnelle supprimée");
    } catch {}
  };

  const toggleNotif = async (key, value) => {
    const prev = { ...notifPrefs };
    setNotifPrefs(p => ({ ...p, [key]: value }));
    try {
      await API.put("/user/notifications", { [key]: value });
    } catch {
      setNotifPrefs(prev);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const role = profile?.role || user?.role || "free";
  const quotas = TIER_QUOTAS[role] || TIER_QUOTAS.free;
  const usage = profile?.usage_today || {};
  const isGoogle = profile?.auth_provider === "google";

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="profile-page">
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <User className="h-6 w-6 text-[var(--ia-navy)]" />
          <h1 className="text-2xl font-bold text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Profil & Paramètres</h1>
        </div>
      </div>

      {/* Abonnement */}
      <Card className="border-[var(--ia-border)] mb-6 animate-fade-in">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Shield className="h-4 w-4 text-[var(--ia-navy)]" /> Abonnement
            </CardTitle>
            <Badge className={`${role === "admin" ? "bg-purple-100 text-purple-700" : role === "premium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
              {role === "admin" ? "Administrateur" : role === "premium" ? "Premium" : "Gratuit"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {role === "free" && (
            <div className="flex items-center justify-between p-3 rounded-md bg-amber-50 border border-amber-200">
              <span className="text-sm text-amber-800">Passez en Premium pour débloquer plus de fonctionnalités</span>
              <Button size="sm" className="bg-[var(--ia-navy)]" onClick={() => navigate("/dashboard/pricing")} data-testid="profile-upgrade-btn">
                <Crown className="h-3.5 w-3.5 mr-1.5" /> Voir les tarifs
              </Button>
            </div>
          )}
          {role === "premium" && (
            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200">
              Vous bénéficiez de l'accès Premium complet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Utilisation du jour */}
      <Card className="border-[var(--ia-border)] mb-6 animate-fade-in">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <BarChart3 className="h-4 w-4 text-[var(--ia-navy)]" /> Utilisation aujourd'hui
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Object.entries(quotas).map(([feature, limit]) => {
              const Icon = FEATURE_ICONS[feature] || MessageSquare;
              const used = usage[feature] || 0;
              const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
              return (
                <div key={feature} className="text-center" data-testid={`usage-${feature}`}>
                  <Icon className="h-4 w-4 mx-auto mb-1.5 text-[var(--ia-muted)]" />
                  <p className="text-xs text-[var(--ia-muted)] mb-1">{FEATURE_LABELS[feature]}</p>
                  <p className="text-lg font-bold text-[var(--ia-text)]">{used}<span className="text-xs font-normal text-[var(--ia-muted)]">/{limit >= 99999 ? "illim." : limit}</span></p>
                  <Progress value={pct} className="h-1 mt-1" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Configuration IA personnelle (Premium/Admin) */}
      {(role === "premium" || role === "admin") && (
        <Card className="border-[var(--ia-border)] mb-6 animate-fade-in">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                <Cpu className="h-4 w-4 text-[var(--ia-navy)]" /> Configuration IA personnelle
              </CardTitle>
              {llmConfig?.configured && (
                <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50 text-[10px]">Active</Badge>
              )}
            </div>
            <CardDescription>
              Utilisez votre propre clé API pour choisir le modèle IA de votre choix. Sans configuration, la config globale s'applique.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {llmConfig?.configured && (
              <Alert className="border-green-200 bg-green-50">
                <Cpu className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-xs text-green-700">
                  Config active : <strong>{LLM_PROVIDERS.find(p => p.provider === llmConfig.provider && p.model === llmConfig.model)?.label || `${llmConfig.provider} / ${llmConfig.model}`}</strong>
                  {llmConfig.has_api_key && <span className="ml-2 text-[var(--ia-muted)]">({llmConfig.api_key_preview})</span>}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Modèle IA</Label>
              <Select value={selectedLLM} onValueChange={setSelectedLLM}>
                <SelectTrigger data-testid="user-llm-select"><SelectValue placeholder="Choisir un modèle..." /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1 text-xs font-semibold text-[var(--ia-muted)]">Google</div>
                  {LLM_PROVIDERS.filter(p => p.provider === "google").map(p => (
                    <SelectItem key={`${p.provider}:${p.model}`} value={`${p.provider}:${p.model}`}>{p.label}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-[var(--ia-muted)] mt-1 border-t pt-1">Mistral</div>
                  {LLM_PROVIDERS.filter(p => p.provider === "mistral").map(p => (
                    <SelectItem key={`${p.provider}:${p.model}`} value={`${p.provider}:${p.model}`}>{p.label}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-[var(--ia-muted)] mt-1 border-t pt-1">DeepSeek</div>
                  {LLM_PROVIDERS.filter(p => p.provider === "deepseek").map(p => (
                    <SelectItem key={`${p.provider}:${p.model}`} value={`${p.provider}:${p.model}`}>{p.label}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-[var(--ia-muted)] mt-1 border-t pt-1">xAI (Grok)</div>
                  {LLM_PROVIDERS.filter(p => p.provider === "grok").map(p => (
                    <SelectItem key={`${p.provider}:${p.model}`} value={`${p.provider}:${p.model}`}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Clé API</Label>
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 text-[var(--ia-muted)] flex-shrink-0" />
                <Input data-testid="user-llm-api-key" type="password" value={llmApiKey} onChange={(e) => setLlmApiKey(e.target.value)}
                  placeholder={llmConfig?.has_api_key ? "Laisser vide pour garder la clé actuelle..." : "Entrez votre clé API..."} />
              </div>
              <p className="text-[10px] text-[var(--ia-muted)]">
                Votre clé est chiffrée (AES) et stockée de manière sécurisée. Elle n'est jamais visible en clair.
              </p>
            </div>

            <div className="flex items-center justify-between">
              {llmConfig?.configured && (
                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={deleteLLMConfig} data-testid="user-llm-delete-btn">
                  <X className="h-3.5 w-3.5 mr-1" /> Supprimer ma config
                </Button>
              )}
              <div className="flex-1" />
              <Button onClick={saveLLMConfig} disabled={savingLLM || !selectedLLM} className="bg-[var(--ia-navy)]" data-testid="user-llm-save-btn">
                {savingLLM ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {role === "free" && (
        <Card className="border-[var(--ia-border)] border-dashed mb-6 animate-fade-in">
          <CardContent className="p-6 text-center">
            <Cpu className="h-8 w-8 text-[var(--ia-muted)] mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700 mb-1">Configuration IA personnelle</p>
            <p className="text-xs text-[var(--ia-muted)] mb-3">Passez en Premium pour utiliser votre propre clé API et choisir votre modèle IA préféré.</p>
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => navigate("/dashboard/pricing")} data-testid="profile-llm-upgrade-btn">
              <Crown className="h-3.5 w-3.5 mr-1.5" /> Passer en Premium
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Notifications & Newsletter */}
      <Card className="border-[var(--ia-border)] mb-6 animate-fade-in">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <Bell className="h-4 w-4 text-[var(--ia-navy)]" /> Notifications & Newsletter
          </CardTitle>
          <CardDescription>Gérez vos préférences de communication</CardDescription>
        </CardHeader>
        <CardContent>
          {notifPrefs && (
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-[var(--ia-blue)]" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Newsletter IA Conforme</p>
                    <p className="text-xs text-[var(--ia-muted)]">Recevez nos analyses et conseils conformité IA chaque mois</p>
                  </div>
                </div>
                <Switch checked={!!notifPrefs.newsletter} onCheckedChange={(v) => toggleNotif("newsletter", v)} data-testid="notif-newsletter-toggle" />
              </div>
              <Separator />
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Alertes de quota</p>
                    <p className="text-xs text-[var(--ia-muted)]">Soyez prévenu quand votre quota quotidien approche la limite</p>
                  </div>
                </div>
                <Switch checked={!!notifPrefs.quota_alerts} onCheckedChange={(v) => toggleNotif("quota_alerts", v)} data-testid="notif-quota-toggle" />
              </div>
              <Separator />
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Nouvelles réglementations</p>
                    <p className="text-xs text-[var(--ia-muted)]">Alertes sur les évolutions AI Act, RGPD et conformité IA</p>
                  </div>
                </div>
                <Switch checked={!!notifPrefs.new_regulations} onCheckedChange={(v) => toggleNotif("new_regulations", v)} data-testid="notif-regulations-toggle" />
              </div>
              <Separator />
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <Cpu className="h-4 w-4 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ia-text)]">Mises à jour produit</p>
                    <p className="text-xs text-[var(--ia-muted)]">Nouvelles fonctionnalités et améliorations de l'application</p>
                  </div>
                </div>
                <Switch checked={!!notifPrefs.product_updates} onCheckedChange={(v) => toggleNotif("product_updates", v)} data-testid="notif-updates-toggle" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informations personnelles */}
      <Card className="border-[var(--ia-border)] mb-6 animate-fade-in">
        <CardHeader className="pb-3">
          <CardTitle className="text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Informations personnelles</CardTitle>
          <CardDescription>{profile?.email} {isGoogle && <Badge variant="outline" className="ml-2 text-[10px]">Google</Badge>}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Secteur d'activité</Label>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger data-testid="profile-sector-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {SECTORS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Taille de structure</Label>
              <Select value={structure} onValueChange={setStructure}>
                <SelectTrigger data-testid="profile-structure-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {STRUCTURES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={savingProfile} className="bg-[var(--ia-navy)]" data-testid="profile-save-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Changement de mot de passe */}
      {!isGoogle && (
        <Card className="border-[var(--ia-border)] mb-6 animate-fade-in">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Lock className="h-4 w-4 text-[var(--ia-navy)]" /> Changer le mot de passe
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <Input data-testid="profile-current-pwd" type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} placeholder="Votre mot de passe actuel" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nouveau mot de passe</Label>
                <Input data-testid="profile-new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Minimum 6 caractères" />
              </div>
              <div className="space-y-2">
                <Label>Confirmer</Label>
                <Input data-testid="profile-confirm-pwd" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} placeholder="Confirmer le mot de passe" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={changePassword} disabled={savingPwd || !currentPwd || !newPwd} variant="outline" data-testid="profile-change-pwd-btn">
                {savingPwd ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />} Modifier
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Portal (Premium users) */}
      {profile?.role === "premium" && (
        <Card className="border-[var(--ia-border)] mb-6 animate-fade-in" data-testid="subscription-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <CreditCard className="h-4 w-4 text-amber-600" /> Mon abonnement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--ia-muted)]">Plan</span>
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">{subscription?.plan || "—"}</Badge>
            </div>
            {subscription?.premium_until && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--ia-muted)]">Renouvellement</span>
                <span className="text-slate-700">{new Date(subscription.premium_until).toLocaleDateString("fr-FR")}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-[var(--ia-border)]">
              <p className="text-xs text-[var(--ia-muted)]">Annuler — vous repasserez en Gratuit immédiatement</p>
              <Button variant="outline" size="sm" onClick={cancelSubscription} disabled={cancelling} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="cancel-sub-btn">
                {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <X className="h-3.5 w-3.5 mr-2" />} Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2FA Admin */}
      {profile?.role === "admin" && (
        <Card className="border-[var(--ia-border)] mb-6 animate-fade-in" data-testid="totp-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Smartphone className="h-4 w-4 text-blue-600" /> Authentification à deux facteurs (2FA)
              {totpStatus?.enabled && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Activée</Badge>}
            </CardTitle>
            <CardDescription>Sécurité renforcée pour les administrateurs avec Google Authenticator, 1Password ou Authy.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!totpStatus?.enabled && !totpSetup && (
              <Button onClick={startTotpSetup} disabled={totpLoading} variant="outline" data-testid="totp-setup-btn">
                {totpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />} Activer la 2FA
              </Button>
            )}
            {totpSetup && !totpStatus?.enabled && (
              <div className="space-y-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm text-slate-700">
                  <strong>1.</strong> Ouvrez Google Authenticator (ou similaire) et scannez ce QR code,
                  ou entrez la clé manuellement.
                </p>
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(totpSetup.otpauth_uri)}`}
                    alt="QR code 2FA"
                    className="rounded border border-slate-200 bg-white p-2"
                    data-testid="totp-qr"
                  />
                  <code className="text-[10px] font-mono bg-white px-2 py-1 rounded border border-slate-200">{totpSetup.secret}</code>
                </div>
                <p className="text-sm text-slate-700"><strong>2.</strong> Entrez le code à 6 chiffres affiché dans l'app :</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength={6}
                    className="font-mono text-center text-lg tracking-widest"
                    data-testid="totp-code-input"
                  />
                  <Button onClick={enableTotp} disabled={totpLoading || totpCode.length !== 6} data-testid="totp-enable-btn">
                    {totpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activer"}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setTotpSetup(null); setTotpCode(""); }}>Annuler</Button>
              </div>
            )}
            {totpStatus?.enabled && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-[var(--ia-muted)]">Vous devrez entrer un code 2FA à chaque connexion.</p>
                <Button variant="outline" size="sm" onClick={disableTotp} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="totp-disable-btn">Désactiver</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* RGPD — Export & droits */}
      <Card className="border-[var(--ia-border)] mb-6 animate-fade-in" data-testid="rgpd-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <FileJson className="h-4 w-4 text-emerald-600" /> Mes données (RGPD)
          </CardTitle>
          <CardDescription>Conformément aux Articles 17 (effacement) et 20 (portabilité) du RGPD.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Télécharger toutes mes données</p>
              <p className="text-xs text-[var(--ia-muted)]">Export JSON complet : profil, conversations, diagnostics, paiements, usage IA…</p>
            </div>
            <Button variant="outline" onClick={exportData} data-testid="rgpd-export-btn">
              <Download className="h-4 w-4 mr-2" /> Télécharger
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Zone dangereuse */}
      <Card className="border-red-200 mb-6 animate-fade-in">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-red-600 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <AlertTriangle className="h-4 w-4" /> Zone dangereuse
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Supprimer le compte</p>
              <p className="text-xs text-[var(--ia-muted)]">Cette action est irréversible. Toutes vos données seront supprimées.</p>
            </div>
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="profile-delete-account-btn">
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Supprimer votre compte ?</DialogTitle>
                  <DialogDescription>
                    Cette action est irréversible. Toutes vos conversations, diagnostics et données seront définitivement supprimés.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annuler</Button>
                  <Button variant="destructive" onClick={deleteAccount} disabled={deleting} data-testid="profile-confirm-delete-btn">
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Supprimer définitivement
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
